import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FileText, ReceiptText, SearchX, ShieldCheck, ShieldPlus, Truck, Undo2 } from 'lucide-react';
import {
  ActionList,
  ActionListItem,
  Alert,
  Button,
  EmptyState,
  KeyValueList,
  KeyValueRow,
  PageHeader,
  ProductThumb,
  StatusPill,
} from '@/components/common';
import { useCustomerOrders } from '@/hooks/useCustomerProducts';
import { useFileClaim } from '@/hooks/useFileClaim';
import type { ProductCase } from '@/types/product';
import { addProtectionNotLive, orderStatus } from '@/utils/orderPresentation';
import { daysUntil, formatDay, formatRupees } from '@/utils/productCases';
import { shipmentPath } from '@/utils/shipment';
import { GST_PATH } from '@/utils/gst';

const BACK = { to: '/customer/orders', label: 'Orders' };

function actionsFor(order: ProductCase, go: (path: string) => void, fileClaim: () => void): ActionListItem[] {
  const ret = order.returnAction;
  const items: ActionListItem[] = [];

  if (order.state === 'protected' && order.plan) {
    items.push(
      { icon: ShieldCheck, title: 'View plan', description: order.plan.name, emphasis: true, onSelect: () => go(`/customer/policies/${order.plan!.policy.id}`) },
      { icon: FileText, title: 'File a claim', description: 'For damage or defects your plan covers', onSelect: fileClaim },
    );
  }
  if (order.state === 'on_the_way') {
    items.push({
      icon: Truck,
      title: 'Track shipment',
      description: [order.shipment?.eta && `Arriving ${order.shipment.eta}`, order.shipment?.detail?.split(' · ')[0]].filter(Boolean).join(' · ') || 'On its way',
      onSelect: () => go(shipmentPath(order.orderId!)),
    });
  }
  if (order.state !== 'protected' && !order.protectionClosedOn) {
    items.push({
      icon: ShieldPlus,
      title: 'Add protection',
      description: order.state === 'on_the_way' ? 'Cover can start today, even before delivery' : 'Accidental Damage or Extended Warranty',
      emphasis: true,
      onSelect: addProtectionNotLive,
    });
  }
  if (order.state !== 'on_the_way') {
    items.push({
      icon: Truck,
      title: 'Shipment details',
      description: order.deliveredAt ? `Delivered on ${formatDay(order.deliveredAt)}` : 'Delivered',
      onSelect: () => go(shipmentPath(order.orderId!)),
    });
  }
  items.push(
    ret
      ? {
          icon: Undo2,
          title: ret.disabled ? 'Return requested' : ret.label === 'Track return' ? 'Track return' : 'Return item',
          description:
            ret.label === 'Track return'
              ? 'See where your return is'
              : order.returnWindow?.endsAt
                ? `Return window open till ${formatDay(order.returnWindow.endsAt)}`
                : ret.label,
          disabled: ret.disabled,
          onSelect: ret.path ? () => go(ret.path!) : undefined,
        }
      : {
          icon: Undo2,
          title: 'Return item',
          description: order.state === 'on_the_way' ? 'Available after delivery' : 'Return window closed',
          disabled: true,
        },
    { icon: ReceiptText, title: 'GST invoice', description: 'For plans you buy on this order', onSelect: () => go(GST_PATH) },
  );
  return items;
}

/** T2A / T2B Order details: what to do next with an order, and its summary. */
const OrderDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { orders, loading, error, reload } = useCustomerOrders({ withShipmentSamples: true });
  const { fileClaim } = useFileClaim();
  const order = orders.find((o) => o.orderId === id);

  if (loading || error || !order) {
    return (
      <div className="tw-flex tw-flex-col tw-gap-6">
        <PageHeader title="Order" back={BACK} />
        {loading ? (
          <div aria-busy className="tw-h-64 tw-animate-pulse tw-rounded-2xl tw-bg-ds-surface" />
        ) : error ? (
          <div className="tw-flex tw-flex-col tw-gap-3">
            <Alert tone="danger" title="Something went wrong">
              {error}
            </Alert>
            <Button variant="secondary" className="tw-self-start" onClick={reload}>
              Try again
            </Button>
          </div>
        ) : (
          <EmptyState
            icon={SearchX}
            title="Order not found"
            description="This order isn’t linked to your mobile number."
            action={<Button onClick={() => navigate(BACK.to)}>Back to orders</Button>}
          />
        )}
      </div>
    );
  }

  // The details header shows delivery status (the protection status is in the alert and actions).
  const { tone, label } =
    order.state === 'on_the_way' ? orderStatus(order) : { tone: 'success' as const, label: order.deliveredAt ? 'Delivered' : 'Ordered' };
  const summary: KeyValueRow[] = [
    order.orderLabel && { label: 'Order ID', value: order.orderLabel },
    order.orderedAt && { label: 'Ordered on', value: formatDay(order.orderedAt) },
    order.deliveredAt && { label: 'Delivered on', value: formatDay(order.deliveredAt) },
    order.price && { label: 'Amount paid', value: formatRupees(order.price) },
    order.plan && { label: 'Plan', value: order.plan.name },
  ].filter(Boolean) as KeyValueRow[];

  return (
    <div className="tw-flex tw-flex-col tw-gap-6">
      <PageHeader
        title={order.title}
        badge={<StatusPill tone={tone}>{label}</StatusPill>}
        subtitle={['Order ' + (order.orderLabel ?? ''), order.merchant].filter(Boolean).join(' · ')}
        back={BACK}
      />

      <div className="tw-grid tw-items-start tw-gap-4 lg:tw-grid-cols-[minmax(0,560fr)_minmax(0,496fr)] lg:tw-gap-6">
        <div className="tw-flex tw-flex-col tw-gap-4">
          {order.state === 'not_protected' && !order.protectionClosedOn && order.protectionDeadline && (
            <Alert tone="warning" title="Not protected yet">
              You can add a plan until {formatDay(order.protectionDeadline)} — {daysUntil(order.protectionDeadline)} days left.
            </Alert>
          )}
          <ActionList items={actionsFor(order, navigate, () => order.plan && fileClaim(order.plan.policy))} />
        </div>

        <section aria-label="Order summary" className="tw-flex tw-flex-col tw-gap-3 tw-rounded-2xl tw-border tw-border-ds-border-default tw-bg-ds-surface tw-p-5">
          <div className="tw-flex tw-items-center tw-gap-3 tw-pb-1">
            <ProductThumb src={order.imageUrl} alt={order.title} size="sm" />
            <div className="tw-flex tw-min-w-0 tw-flex-col tw-gap-0.5">
              <p className="tw-text-[15px] tw-font-semibold tw-leading-5 tw-text-ds-text-primary">{order.title}</p>
              {order.merchant && <p className="tw-text-[13px] tw-leading-[18px] tw-text-ds-text-secondary">{order.merchant}</p>}
            </div>
          </div>
          <KeyValueList rows={summary} />
        </section>
      </div>
    </div>
  );
};

export default OrderDetailsPage;
